/**
 * The little document glyph beside a file, coloured and lettered by its type.
 *
 * A generic "file" icon says nothing a member needs; the extension is what tells
 * them whether it opens in a viewer, a spreadsheet, or a photo app before they
 * download. Drawn inline rather than shipped as one image per type: an SVG needs
 * no request, takes the row's size, and adding a type is a line in a table.
 *
 * Only PDF is accepted as an event document today. The table already knows the
 * office and image types so the badge is ready the day another one is allowed,
 * and so the same component can label speaker flyers and member files.
 */
const FILE_TYPES: Record<string, { label: string; fill: string; ink: string }> = {
  pdf: { label: "PDF", fill: "#D64545", ink: "#FFFFFF" },
  doc: { label: "DOC", fill: "#2B6CB0", ink: "#FFFFFF" },
  docx: { label: "DOCX", fill: "#2B6CB0", ink: "#FFFFFF" },
  xls: { label: "XLS", fill: "#1F8A4C", ink: "#FFFFFF" },
  xlsx: { label: "XLSX", fill: "#1F8A4C", ink: "#FFFFFF" },
  csv: { label: "CSV", fill: "#1F8A4C", ink: "#FFFFFF" },
  ppt: { label: "PPT", fill: "#D2691E", ink: "#FFFFFF" },
  pptx: { label: "PPTX", fill: "#D2691E", ink: "#FFFFFF" },
  key: { label: "KEY", fill: "#2563EB", ink: "#FFFFFF" },
  txt: { label: "TXT", fill: "#4B5563", ink: "#FFFFFF" },
  md: { label: "MD", fill: "#4B5563", ink: "#FFFFFF" },
  jpg: { label: "JPG", fill: "#7C3AED", ink: "#FFFFFF" },
  jpeg: { label: "JPG", fill: "#7C3AED", ink: "#FFFFFF" },
  png: { label: "PNG", fill: "#7C3AED", ink: "#FFFFFF" },
  webp: { label: "WEBP", fill: "#7C3AED", ink: "#FFFFFF" },
  gif: { label: "GIF", fill: "#7C3AED", ink: "#FFFFFF" },
  svg: { label: "SVG", fill: "#7C3AED", ink: "#FFFFFF" },
  mp4: { label: "MP4", fill: "#0E7490", ink: "#FFFFFF" },
  mov: { label: "MOV", fill: "#0E7490", ink: "#FFFFFF" },
  zip: { label: "ZIP", fill: "#6B7280", ink: "#FFFFFF" },
};

const UNKNOWN = { label: "FILE", fill: "#475569", ink: "#FFFFFF" };

/** The extension a file name ends with, lower-cased, or "" when it has none. */
export function fileExtension(fileName: string): string {
  const match = /\.([a-z0-9]{1,5})$/i.exec(fileName.trim());
  return match ? match[1].toLowerCase() : "";
}

export default function FileTypeBadge({ fileName, size = 32 }: { fileName: string; size?: number }) {
  const type = FILE_TYPES[fileExtension(fileName)] ?? UNKNOWN;
  // Four letters have to fit the same width as three, so the longer label is set smaller.
  const fontSize = type.label.length > 3 ? 7.2 : 8.6;
  const height = Math.round(size * 1.2);

  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 32 38"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flex: "0 0 auto" }}
    >
      {/* The sheet, with the classic folded corner so it reads as a document at a glance. */}
      <path d="M4 1.5h16l8 8V34.5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-31a2 2 0 0 1 2-2z" fill="#F8FAFC" />
      <path d="M20 1.5v8h8z" fill="#CBD5E1" />
      <path d="M4 1.5h16l8 8V34.5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-31a2 2 0 0 1 2-2z" fill="none" stroke="rgba(15,23,42,.35)" strokeWidth=".8" />
      {/* The type label on its coloured band. */}
      <rect x="0" y="19" width="26" height="12.5" rx="2.2" fill={type.fill} />
      <text
        x="13"
        y="28.4"
        textAnchor="middle"
        fontFamily="'Space Grotesk', 'DM Sans', sans-serif"
        fontSize={fontSize}
        fontWeight={800}
        letterSpacing=".04em"
        fill={type.ink}
      >
        {type.label}
      </text>
    </svg>
  );
}
